import axios, { AxiosInstance } from "axios";
import API from "../Utils/API/API";
import workPolicyApplicationRoutes from "../routes/work-policy-application-routes";
import {
  ApprovalStaggeredTime,
  EditableWorkPolicyApplicationDetail,
  HistoricalWorkPolicyApplicationDetail,
  isSupportedEligiblePolicy,
  RawEligibleWorkPolicy,
  SettlementPeriod,
  WorkPolicyApplicationApi,
  WorkPolicyApplicationAxiosRequest,
  WorkPolicyApplicationClientState,
  WorkPolicyApplicationFailureEnvelope,
  WorkPolicyApplicationRequest,
  WorkPolicyApplicationStage,
  WorkPolicyApplicationSuccessEnvelope,
  WorkPolicyApplicationValidationIssue
} from "./work-policy-application-api.types";

function reconciliationState(
  failure: WorkPolicyApplicationFailureEnvelope,
  knownResourceId?: number,
  networkUnknown = false
): WorkPolicyApplicationClientState {
  const resourceId = failure.resourceId ?? knownResourceId;
  if (
    failure.code === "WORK_POLICY_VALIDATION" &&
    failure.issues?.some(issue => /(^|\.)workType$/.test(issue.path))
  ) {
    return { kind: "unsupported-policy" };
  }
  if (
    networkUnknown ||
    failure.code === "WORK_POLICY_STAGE_FAILED" ||
    failure.code === "WORK_POLICY_STALE"
  ) {
    return {
      kind: "reconcile-before-retry",
      ...(resourceId !== undefined ? { resourceId } : {}),
      ...(failure.lastCompletedStage
        ? { lastCompletedStage: failure.lastCompletedStage }
        : {}),
      ...(failure.fingerprint ? { fingerprint: failure.fingerprint } : {})
    };
  }
  return { kind: "failed" };
}

export class WorkPolicyApplicationApiError extends Error {
  readonly code: string;
  readonly status?: number;
  readonly resourceId?: number;
  readonly lastCompletedStage?: WorkPolicyApplicationStage;
  readonly retryable: boolean;
  readonly fingerprint?: string;
  readonly issues?: WorkPolicyApplicationValidationIssue[];
  readonly networkUnknown: boolean;
  readonly clientState: WorkPolicyApplicationClientState;

  constructor(
    failure: WorkPolicyApplicationFailureEnvelope,
    status?: number,
    knownResourceId?: number,
    networkUnknown = false
  ) {
    super(failure.message);
    this.name = "WorkPolicyApplicationApiError";
    this.code = failure.code;
    this.status = status;
    this.resourceId = failure.resourceId ?? knownResourceId;
    this.lastCompletedStage = failure.lastCompletedStage;
    this.retryable = failure.retryable;
    this.fingerprint = failure.fingerprint;
    this.issues = failure.issues;
    this.networkUnknown = networkUnknown;
    this.clientState = reconciliationState(
      failure,
      knownResourceId,
      networkUnknown
    );
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

async function execute<T>(
  request: WorkPolicyApplicationAxiosRequest<T>,
  knownResourceId?: number
): Promise<WorkPolicyApplicationSuccessEnvelope<T>> {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const failure = error.response.data as WorkPolicyApplicationFailureEnvelope;
      if (failure && failure.ok === false && typeof failure.code === "string") {
        throw new WorkPolicyApplicationApiError(
          failure,
          error.response.status,
          knownResourceId
        );
      }
    }
    throw new WorkPolicyApplicationApiError(
      {
        ok: false,
        code: "WORK_POLICY_NETWORK_UNKNOWN",
        message: "서버 응답을 확인하지 못했습니다.",
        retryable: knownResourceId !== undefined
      },
      undefined,
      knownResourceId,
      true
    );
  }
}

export function createWorkPolicyApplicationApi(
  client: AxiosInstance
): WorkPolicyApplicationApi {
  return {
    eligible: async () => {
      const response = await execute<RawEligibleWorkPolicy[]>(
        client.get(workPolicyApplicationRoutes.eligible)
      );
      return {
        ...response,
        data: response.data.filter(isSupportedEligiblePolicy)
      };
    },
    staggeredTimes: (workPolicyId: number) =>
      execute<ApprovalStaggeredTime[]>(
        client.get(workPolicyApplicationRoutes.staggeredTimes(workPolicyId))
      ),
    settlementPeriods: (startDate: string, endDate: string) =>
      execute<SettlementPeriod[]>(
        client.get(workPolicyApplicationRoutes.settlementPeriods, {
          params: { startDate, endDate }
        })
      ),
    detail: (approvalRequestIdx: number) =>
      execute<HistoricalWorkPolicyApplicationDetail>(
        client.get(workPolicyApplicationRoutes.detail(approvalRequestIdx)),
        approvalRequestIdx
      ),
    createDraft: (request: WorkPolicyApplicationRequest) =>
      execute<EditableWorkPolicyApplicationDetail>(
        client.post(workPolicyApplicationRoutes.drafts, request)
      ),
    updateDraft: (
      approvalRequestIdx: number,
      request: WorkPolicyApplicationRequest
    ) =>
      execute<EditableWorkPolicyApplicationDetail>(
        client.put(workPolicyApplicationRoutes.draft(approvalRequestIdx), request),
        approvalRequestIdx
      ),
    createSubmission: (request: WorkPolicyApplicationRequest) =>
      execute<EditableWorkPolicyApplicationDetail>(
        client.post(workPolicyApplicationRoutes.submissions, request)
      ),
    submitDraft: (
      approvalRequestIdx: number,
      request: WorkPolicyApplicationRequest
    ) =>
      execute<EditableWorkPolicyApplicationDetail>(
        client.post(workPolicyApplicationRoutes.submit(approvalRequestIdx), request),
        approvalRequestIdx
      ),
    resume: (
      approvalRequestIdx: number,
      request: WorkPolicyApplicationRequest
    ) =>
      execute<EditableWorkPolicyApplicationDetail>(
        client.post(workPolicyApplicationRoutes.resume(approvalRequestIdx), request),
        approvalRequestIdx
      )
  };
}

const workPolicyApplicationApi = createWorkPolicyApplicationApi(API());

export default workPolicyApplicationApi;
